import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { userCan } from '../utils/permissionUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Settings as SettingsIcon, 
  Mail, 
  Clock, 
  Send, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  Play,
  RefreshCw,
  Eye,
  Globe,
  Database,
  ArrowRight,
  Phone,
  CreditCard,
  User,
  DollarSign,
  Percent,
  Save,
  FileText
} from 'lucide-react';
import { 
  formatUAEMobile, 
  formatEmiratesId, 
  formatName,
  displayUAEMobile,
  displayEmiratesId
} from '../utils/formatUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';
import StandardDocuments from '../components/StandardDocuments';
import { formatErrorMessage } from '../utils/formatError';

const Settings = ({ user }) => {
  const { isMobile, isTablet } = useDeviceDetection();
  const [activeSection, setActiveSection] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    enabled: true,
    schedule_hour: 8,
    schedule_minute: 0,
    timezone_offset: 4,
    send_empty_digest: false,
    scheduler_running: false,
    last_sent: null
  });
  
  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Data Standardization state
  const [standardizationPreviewOpen, setStandardizationPreviewOpen] = useState(false);
  const [standardizationPreview, setStandardizationPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [migratingData, setMigratingData] = useState(false);

  // Admin Fee Config state
  const [adminFeeDialogOpen, setAdminFeeDialogOpen] = useState(false);
  const [adminFeeConfig, setAdminFeeConfig] = useState({
    fines: { fee_type: 'fixed', fixed_amount: 0, percentage: 0 },
    salik: { fee_type: 'fixed', fixed_amount: 0.50, percentage: 0 }
  });
  const [savingFeeConfig, setSavingFeeConfig] = useState(false);
  
  // Fee application checkboxes state
  const [feeApplicationOptions, setFeeApplicationOptions] = useState({
    finesNewRecords: true,
    finesOldRecords: false,
    salikNewRecords: true,
    salikOldRecords: false
  });

  useEffect(() => {
    fetchEmailSettings();
    fetchAdminFeeConfig();
  }, []);

  // Fetch admin fee config
  const fetchAdminFeeConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/fee-config`, {
        headers: getAuthHeader()
      });
      setAdminFeeConfig(response.data);
    } catch (error) {
      console.error('Error fetching admin fee config:', error);
    }
  };

  // Admin fee application scope state
  const [feeApplicationDialogOpen, setFeeApplicationDialogOpen] = useState(false);
  const [applyingToExisting, setApplyingToExisting] = useState(false);

  // Open application scope dialog (called when clicking Configure Admin Fees button)
  const openFeeApplicationDialog = () => {
    // Reset checkboxes to defaults
    setFeeApplicationOptions({
      finesNewRecords: true,
      finesOldRecords: false,
      salikNewRecords: true,
      salikOldRecords: false
    });
    setFeeApplicationDialogOpen(true);
  };

  // Apply admin fees based on selected options
  const applyFeeConfiguration = async () => {
    try {
      setApplyingToExisting(true);
      const results = [];
      
      // Save the configuration first (applies to new records automatically)
      await axios.post(`${API}/admin/fee-config`, adminFeeConfig, {
        headers: getAuthHeader()
      });
      
      if (feeApplicationOptions.finesNewRecords) {
        results.push('Fines: New records will use updated config');
      }
      
      if (feeApplicationOptions.salikNewRecords) {
        results.push('SALIK: New records will use updated config');
      }
      
      // Apply to old fines if selected
      if (feeApplicationOptions.finesOldRecords) {
        const finesRes = await axios.post(
          `${API}/admin/recalculate-fees/fines`,
          {},
          { headers: getAuthHeader() }
        );
        results.push(`Fines: ${finesRes.data.updated_count} existing records updated`);
      }
      
      // Apply to old SALIK if selected
      if (feeApplicationOptions.salikOldRecords) {
        const salikRes = await axios.post(
          `${API}/admin/recalculate-fees/salik`,
          {},
          { headers: getAuthHeader() }
        );
        results.push(`SALIK: ${salikRes.data.updated_count} existing records updated`);
      }
      
      toast.success('Configuration applied successfully!');
      setFeeApplicationDialogOpen(false);
      setAdminFeeDialogOpen(false);
      fetchAdminFeeConfig(); // Refresh the config display
    } catch (error) {
      console.error('Error applying fee configuration:', error);
      toast.error(formatErrorMessage(error, 'Failed to apply configuration'));
    } finally {
      setApplyingToExisting(false);
    }
  };

  // Toggle checkbox
  const toggleFeeOption = (option) => {
    setFeeApplicationOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  // Check if at least one option is selected
  const hasSelectedOptions = feeApplicationOptions.finesNewRecords || 
    feeApplicationOptions.finesOldRecords || 
    feeApplicationOptions.salikNewRecords || 
    feeApplicationOptions.salikOldRecords;

  // Fetch preview of data that would be standardized
  const fetchStandardizationPreview = async () => {
    try {
      setLoadingPreview(true);
      
      // Fetch all collections to show preview
      const [ridersRes, employeesRes, tapRes, lesseesRes] = await Promise.all([
        axios.get(`${API}/riders/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/talent/employees`, { headers: getAuthHeader() }),
        axios.get(`${API}/tap/candidates`, { headers: getAuthHeader() }),
        axios.get(`${API}/lessees`, { headers: getAuthHeader() })
      ]);

      const riders = ridersRes.data || [];
      const employees = employeesRes.data?.employees || employeesRes.data || [];
      const tapCandidates = tapRes.data?.candidates || tapRes.data || [];
      const lessees = lesseesRes.data || [];

      // Generate preview of changes for Riders
      const riderChanges = riders.slice(0, 10).map(rider => {
        const newMobile = formatUAEMobile(rider.mobile_uae);
        const newEmiratesId = formatEmiratesId(rider.emirates_id);
        const newName = formatName(rider.name);
        
        return {
          id: rider.id,
          type: 'Rider',
          name: {
            before: rider.name || '',
            after: newName,
            changed: rider.name !== newName
          },
          mobile: {
            before: rider.mobile_uae || '',
            after: newMobile,
            changed: rider.mobile_uae !== newMobile
          },
          emirates_id: {
            before: rider.emirates_id || '',
            after: newEmiratesId,
            changed: rider.emirates_id !== newEmiratesId
          }
        };
      }).filter(r => r.name.changed || r.mobile.changed || r.emirates_id.changed);

      // Generate preview for Staff Employees
      const staffChanges = employees.filter(e => e.employee_type === 'staff').slice(0, 10).map(emp => {
        const newMobile = formatUAEMobile(emp.mobile || emp.mobile_uae);
        const newEmiratesId = formatEmiratesId(emp.emirates_id);
        const newName = formatName(emp.name);
        
        return {
          id: emp.id,
          type: 'Staff',
          name: {
            before: emp.name || '',
            after: newName,
            changed: emp.name !== newName
          },
          mobile: {
            before: emp.mobile || emp.mobile_uae || '',
            after: newMobile,
            changed: (emp.mobile || emp.mobile_uae) !== newMobile
          },
          emirates_id: {
            before: emp.emirates_id || '',
            after: newEmiratesId,
            changed: emp.emirates_id !== newEmiratesId
          }
        };
      }).filter(e => e.name.changed || e.mobile.changed || e.emirates_id.changed);

      // Generate preview for TAP Candidates
      const tapChanges = tapCandidates.slice(0, 10).map(candidate => {
        const newMobile = formatUAEMobile(candidate.mobile || candidate.mobile_uae);
        const newEmiratesId = formatEmiratesId(candidate.emirates_id);
        const newName = formatName(candidate.name);
        
        return {
          id: candidate.id,
          type: 'TAP Candidate',
          name: {
            before: candidate.name || '',
            after: newName,
            changed: candidate.name !== newName
          },
          mobile: {
            before: candidate.mobile || candidate.mobile_uae || '',
            after: newMobile,
            changed: (candidate.mobile || candidate.mobile_uae) !== newMobile
          },
          emirates_id: {
            before: candidate.emirates_id || '',
            after: newEmiratesId,
            changed: candidate.emirates_id !== newEmiratesId
          }
        };
      }).filter(c => c.name.changed || c.mobile.changed || c.emirates_id.changed);

      // Generate preview for Lessees
      const lesseeChanges = lessees.slice(0, 10).map(lessee => {
        const newPhone = formatUAEMobile(lessee.phone);
        const newName = formatName(lessee.name);
        const newContactPerson = formatName(lessee.contact_person);
        
        return {
          id: lessee.id,
          type: 'Lessee',
          name: {
            before: lessee.name || '',
            after: newName,
            changed: lessee.name !== newName
          },
          mobile: {
            before: lessee.phone || '',
            after: newPhone,
            changed: lessee.phone !== newPhone
          },
          contact_person: {
            before: lessee.contact_person || '',
            after: newContactPerson,
            changed: lessee.contact_person !== newContactPerson
          }
        };
      }).filter(l => l.name.changed || l.mobile.changed || l.contact_person?.changed);

      // Count total changes
      const totalRidersWithChanges = riders.filter(rider => {
        const newMobile = formatUAEMobile(rider.mobile_uae);
        const newEmiratesId = formatEmiratesId(rider.emirates_id);
        const newName = formatName(rider.name);
        return rider.name !== newName || rider.mobile_uae !== newMobile || rider.emirates_id !== newEmiratesId;
      }).length;

      const staffEmployees = employees.filter(e => e.employee_type === 'staff');
      const totalStaffWithChanges = staffEmployees.filter(emp => {
        const newMobile = formatUAEMobile(emp.mobile || emp.mobile_uae);
        const newEmiratesId = formatEmiratesId(emp.emirates_id);
        const newName = formatName(emp.name);
        return emp.name !== newName || (emp.mobile || emp.mobile_uae) !== newMobile || emp.emirates_id !== newEmiratesId;
      }).length;

      const totalTapWithChanges = tapCandidates.filter(candidate => {
        const newMobile = formatUAEMobile(candidate.mobile || candidate.mobile_uae);
        const newEmiratesId = formatEmiratesId(candidate.emirates_id);
        const newName = formatName(candidate.name);
        return candidate.name !== newName || (candidate.mobile || candidate.mobile_uae) !== newMobile || candidate.emirates_id !== newEmiratesId;
      }).length;

      const totalLesseesWithChanges = lessees.filter(lessee => {
        const newPhone = formatUAEMobile(lessee.phone);
        const newName = formatName(lessee.name);
        const newContactPerson = formatName(lessee.contact_person);
        return lessee.name !== newName || lessee.phone !== newPhone || lessee.contact_person !== newContactPerson;
      }).length;

      setStandardizationPreview({
        riders: riderChanges,
        staff: staffChanges,
        tapCandidates: tapChanges,
        lessees: lesseeChanges,
        totalRiders: riders.length,
        totalStaff: staffEmployees.length,
        totalTapCandidates: tapCandidates.length,
        totalLessees: lessees.length,
        ridersWithChanges: totalRidersWithChanges,
        staffWithChanges: totalStaffWithChanges,
        tapWithChanges: totalTapWithChanges,
        lesseesWithChanges: totalLesseesWithChanges
      });
      setStandardizationPreviewOpen(true);
    } catch (error) {
      console.error('Error fetching standardization preview:', error);
      toast.error('Failed to fetch data preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Run the actual data migration
  const runDataStandardization = async () => {
    if (!window.confirm('This will update all existing data to the new standard formats across the entire portal. Are you sure?')) {
      return;
    }
    
    try {
      setMigratingData(true);
      const response = await axios.post(`${API}/admin/standardize-data`, {}, {
        headers: getAuthHeader()
      });
      
      const stats = response.data.statistics || response.data;
      toast.success(
        `Data standardized successfully!\n` +
        `Riders: ${stats.riders?.updated || 0}/${stats.riders?.total || 0}\n` +
        `Staff: ${stats.staff_employees?.updated || 0}/${stats.staff_employees?.total || 0}\n` +
        `TAP Candidates: ${stats.tap_candidates?.updated || 0}/${stats.tap_candidates?.total || 0}\n` +
        `Lessees: ${stats.lessees?.updated || 0}/${stats.lessees?.total || 0}`
      );
      setStandardizationPreviewOpen(false);
    } catch (error) {
      console.error('Error running standardization:', error);
      toast.error(formatErrorMessage(error, 'Failed to standardize data'));
    } finally {
      setMigratingData(false);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/email-settings/critical-alerts`, {
        headers: getAuthHeader()
      });
      setEmailSettings(response.data);
    } catch (error) {
      console.error('Error fetching email settings:', error);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const response = await axios.put(`${API}/email-settings/critical-alerts`, {
        enabled: emailSettings.enabled,
        schedule_hour: emailSettings.schedule_hour,
        schedule_minute: emailSettings.schedule_minute,
        timezone_offset: emailSettings.timezone_offset,
        send_empty_digest: emailSettings.send_empty_digest
      }, {
        headers: getAuthHeader()
      });
      
      if (response.data.success) {
        toast.success(response.data.message || 'Email settings saved successfully');
        setEmailSettings(prev => ({ ...prev, ...response.data.settings }));
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast.error(formatErrorMessage(error, 'Failed to save email settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendDigestNow = async () => {
    try {
      setSendingDigest(true);
      const response = await axios.post(`${API}/email-settings/send-digest-now`, {}, {
        headers: getAuthHeader()
      });
      
      if (response.data.success) {
        const result = response.data.result;
        toast.success(`Digest emails sent: ${result.sent} sent, ${result.skipped} skipped, ${result.failed} failed`);
      }
    } catch (error) {
      console.error('Error sending digest:', error);
      toast.error(formatErrorMessage(error, 'Failed to send digest emails'));
    } finally {
      setSendingDigest(false);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setSendingTest(true);
      const response = await axios.post(`${API}/email-settings/test-email`, {}, {
        headers: getAuthHeader()
      });
      
      if (response.data.success) {
        toast.success(response.data.message || 'Test email sent successfully');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(formatErrorMessage(error, 'Failed to send test email'));
    } finally {
      setSendingTest(false);
    }
  };

  const handlePreviewDigest = async () => {
    try {
      setPreviewLoading(true);
      const response = await axios.post(`${API}/email-settings/preview-digest`, {}, {
        headers: getAuthHeader()
      });
      setPreviewData(response.data);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Error previewing digest:', error);
      toast.error(formatErrorMessage(error, 'Failed to preview digest'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatTime = (hour, minute) => {
    const h = hour.toString().padStart(2, '0');
    const m = minute.toString().padStart(2, '0');
    return `${h}:${m}`;
  };

  const formatLastSent = (lastSent) => {
    if (!lastSent) return 'Never';
    try {
      const date = new Date(lastSent);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown';
    }
  };

  const getTimezoneLabel = (offset) => {
    const sign = offset >= 0 ? '+' : '-';
    return `UTC${sign}${Math.abs(offset)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-lime-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 flex items-center gap-2">
            <SettingsIcon className="w-6 h-6 sm:w-7 sm:h-7 text-lime-600" />
            Settings
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Manage application settings and configurations</p>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="flex gap-2 border-b pb-4 overflow-x-auto">
        <Button
          variant={activeSection === 'general' ? 'default' : 'outline'}
          onClick={() => setActiveSection('general')}
          className="gap-2 whitespace-nowrap"
          size={isMobile ? 'sm' : 'default'}
        >
          <SettingsIcon className="w-4 h-4" />
          General
        </Button>
        <Button
          variant={activeSection === 'documents' ? 'default' : 'outline'}
          onClick={() => setActiveSection('documents')}
          className="gap-2 whitespace-nowrap"
          size={isMobile ? 'sm' : 'default'}
        >
          <FileText className="w-4 h-4" />
          Standard Documents
        </Button>
      </div>

      {/* Standard Documents Section */}
      {activeSection === 'documents' && (
        <StandardDocuments user={user} />
      )}

      {/* General Settings Section */}
      {activeSection === 'general' && (
      <>
      {/* Email Alerts Settings Card */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Mail className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-base sm:text-lg">Critical Alerts Email Digest</CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Configure daily email notifications for critical alerts
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {emailSettings.scheduler_running ? (
                <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {isMobile ? 'Running' : 'Scheduler Running'}
                </Badge>
              ) : (
                <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  {isMobile ? 'Stopped' : 'Scheduler Stopped'}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Left Column - Settings */}
            <div className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="font-medium">Enable Daily Digest</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Send daily summary emails to users with critical alerts
                  </p>
                </div>
                <Switch
                  checked={emailSettings.enabled}
                  onCheckedChange={(checked) => 
                    setEmailSettings(prev => ({ ...prev, enabled: checked }))
                  }
                />
              </div>

              {/* Schedule Time */}
              <div className="space-y-3">
                <Label className="font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-500" />
                  Schedule Time
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Hour (0-23)</Label>
                    <Select
                      value={emailSettings.schedule_hour.toString()}
                      onValueChange={(value) => 
                        setEmailSettings(prev => ({ ...prev, schedule_hour: parseInt(value) }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => (
                          <SelectItem key={i} value={i.toString()}>
                            {i.toString().padStart(2, '0')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Minute (0-59)</Label>
                    <Select
                      value={emailSettings.schedule_minute.toString()}
                      onValueChange={(value) => 
                        setEmailSettings(prev => ({ ...prev, schedule_minute: parseInt(value) }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 15, 30, 45].map((min) => (
                          <SelectItem key={min} value={min.toString()}>
                            {min.toString().padStart(2, '0')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-sm text-slate-500">
                  Scheduled at: <span className="font-medium">{formatTime(emailSettings.schedule_hour, emailSettings.schedule_minute)}</span>
                </p>
              </div>

              {/* Timezone */}
              <div className="space-y-3">
                <Label className="font-medium flex items-center gap-2">
                  <Globe className="w-4 h-4 text-slate-500" />
                  Timezone
                </Label>
                <Select
                  value={emailSettings.timezone_offset.toString()}
                  onValueChange={(value) => 
                    setEmailSettings(prev => ({ ...prev, timezone_offset: parseInt(value) }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">UTC+0 (London)</SelectItem>
                    <SelectItem value="1">UTC+1 (Paris)</SelectItem>
                    <SelectItem value="2">UTC+2 (Cairo)</SelectItem>
                    <SelectItem value="3">UTC+3 (Riyadh)</SelectItem>
                    <SelectItem value="4">UTC+4 (Dubai/UAE)</SelectItem>
                    <SelectItem value="5">UTC+5 (Karachi)</SelectItem>
                    <SelectItem value="5.5">UTC+5:30 (Mumbai)</SelectItem>
                    <SelectItem value="8">UTC+8 (Singapore)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-slate-500">
                  Current timezone: <span className="font-medium">{getTimezoneLabel(emailSettings.timezone_offset)}</span>
                </p>
              </div>

              {/* Send Empty Digest */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="font-medium">Send Empty Digest</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Send email even when there are no critical alerts
                  </p>
                </div>
                <Switch
                  checked={emailSettings.send_empty_digest}
                  onCheckedChange={(checked) => 
                    setEmailSettings(prev => ({ ...prev, send_empty_digest: checked }))
                  }
                />
              </div>

              {/* Save Button */}
              <Button 
                onClick={handleSaveSettings}
                disabled={saving}
                className="w-full bg-lime-600 hover:bg-lime-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>

            {/* Right Column - Actions & Info */}
            <div className="space-y-6">
              {/* Status Info */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">Schedule Information</h4>
                <div className="space-y-2 text-sm text-blue-700">
                  <p>
                    <span className="font-medium">Next Send:</span> Daily at {formatTime(emailSettings.schedule_hour, emailSettings.schedule_minute)} {getTimezoneLabel(emailSettings.timezone_offset)}
                  </p>
                  <p>
                    <span className="font-medium">Last Sent:</span> {formatLastSent(emailSettings.last_sent)}
                  </p>
                  <p>
                    <span className="font-medium">Status:</span> {emailSettings.enabled ? 'Active' : 'Disabled'}
                  </p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="space-y-3">
                <Label className="font-medium">Quick Actions</Label>
                
                {/* Preview Digest */}
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handlePreviewDigest}
                  disabled={previewLoading}
                >
                  {previewLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-2 text-blue-600" />
                  )}
                  Preview Digest Content
                </Button>

                {/* Send Test Email */}
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleSendTestEmail}
                  disabled={sendingTest}
                >
                  {sendingTest ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2 text-green-600" />
                  )}
                  Send Test Email to Me
                </Button>

                {/* Send Digest Now */}
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-orange-200 hover:bg-orange-50"
                  onClick={handleSendDigestNow}
                  disabled={sendingDigest}
                >
                  {sendingDigest ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-2 text-orange-600" />
                  )}
                  Send Digest to All Users Now
                </Button>

                {/* Refresh */}
                <Button 
                  variant="ghost" 
                  className="w-full justify-start"
                  onClick={fetchEmailSettings}
                >
                  <RefreshCw className="w-4 h-4 mr-2 text-slate-500" />
                  Refresh Settings
                </Button>
              </div>

              {/* Help Info */}
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-medium text-yellow-800 mb-2">ℹ️ How it works</h4>
                <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                  <li>Digest emails are sent daily at the scheduled time</li>
                  <li>Each user receives alerts relevant to their role/lessee</li>
                  <li>Alerts include: battery mismatches, document expiry, pending maintenance</li>
                  <li>Use Preview to see what you would receive</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admin Fee Configuration Card */}
      {userCan(user, 'adminTools', 'view', 'settings') && (
        <Card data-testid="admin-fee-config-card">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Admin Fee Configuration</CardTitle>
                  <CardDescription>
                    Configure admin fee calculation for Fines and SALIK
                  </CardDescription>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-700 border-green-200">
                Admin Only
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Fines Config Summary */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <h4 className="font-medium text-red-800">Traffic Fines</h4>
                </div>
                <div className="text-sm text-red-700">
                  <p>Current Setting:</p>
                  <p className="font-semibold mt-1">
                    {adminFeeConfig?.fines?.fee_type === 'percentage'
                      ? `${adminFeeConfig?.fines?.percentage || 0}% of fine amount`
                      : `Fixed Dh ${adminFeeConfig?.fines?.fixed_amount || 0}`}
                  </p>
                </div>
              </div>

              {/* Salik Config Summary */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                  <h4 className="font-medium text-purple-800">SALIK Tolls</h4>
                </div>
                <div className="text-sm text-purple-700">
                  <p>Current Setting:</p>
                  <p className="font-semibold mt-1">
                    {adminFeeConfig?.salik?.fee_type === 'percentage'
                      ? `${adminFeeConfig?.salik?.percentage || 0}% of toll amount`
                      : `Fixed Dh ${adminFeeConfig?.salik?.fixed_amount || 0}`}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                onClick={() => setAdminFeeDialogOpen(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="configure-admin-fees-btn"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Configure Admin Fees
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Fee Configuration Dialog */}
      <Dialog open={adminFeeDialogOpen} onOpenChange={setAdminFeeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Configure Admin Fees
            </DialogTitle>
            <DialogDescription>
              Set admin fee calculation method for Fines and SALIK
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Fines Configuration */}
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-medium text-red-800 mb-4 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Traffic Fines Admin Fee
              </h4>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Label className="w-24">Fee Type:</Label>
                  <Select
                    value={adminFeeConfig?.fines?.fee_type || 'fixed'}
                    onValueChange={(value) => setAdminFeeConfig(prev => ({
                      ...prev,
                      fines: { ...prev.fines, fee_type: value }
                    }))}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                      <SelectItem value="percentage">Percentage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {adminFeeConfig?.fines?.fee_type === 'percentage' ? (
                  <div className="flex items-center gap-4">
                    <Label className="w-24">Percentage:</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={adminFeeConfig?.fines?.percentage || 0}
                        onChange={(e) => setAdminFeeConfig(prev => ({
                          ...prev,
                          fines: { ...prev.fines, percentage: parseFloat(e.target.value) || 0 }
                        }))}
                        className="w-24"
                        data-testid="fines-percentage-input"
                      />
                      <Percent className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <Label className="w-24">Amount:</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">Dh</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={adminFeeConfig?.fines?.fixed_amount || 0}
                        onChange={(e) => setAdminFeeConfig(prev => ({
                          ...prev,
                          fines: { ...prev.fines, fixed_amount: parseFloat(e.target.value) || 0 }
                        }))}
                        className="w-24"
                        data-testid="fines-fixed-amount-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* SALIK Configuration */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-800 mb-4 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                SALIK Tolls Admin Fee
              </h4>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Label className="w-24">Fee Type:</Label>
                  <Select
                    value={adminFeeConfig?.salik?.fee_type || 'fixed'}
                    onValueChange={(value) => setAdminFeeConfig(prev => ({
                      ...prev,
                      salik: { ...prev.salik, fee_type: value }
                    }))}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                      <SelectItem value="percentage">Percentage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {adminFeeConfig?.salik?.fee_type === 'percentage' ? (
                  <div className="flex items-center gap-4">
                    <Label className="w-24">Percentage:</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={adminFeeConfig?.salik?.percentage || 0}
                        onChange={(e) => setAdminFeeConfig(prev => ({
                          ...prev,
                          salik: { ...prev.salik, percentage: parseFloat(e.target.value) || 0 }
                        }))}
                        className="w-24"
                        data-testid="salik-percentage-input"
                      />
                      <Percent className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <Label className="w-24">Amount:</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">Dh</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={adminFeeConfig?.salik?.fixed_amount || 0}
                        onChange={(e) => setAdminFeeConfig(prev => ({
                          ...prev,
                          salik: { ...prev.salik, fixed_amount: parseFloat(e.target.value) || 0 }
                        }))}
                        className="w-24"
                        data-testid="salik-fixed-amount-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setAdminFeeDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={openFeeApplicationDialog}
                className="bg-green-600 hover:bg-green-700"
                data-testid="save-admin-fee-config-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Configuration
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fee Application Scope Dialog */}
      <Dialog open={feeApplicationDialogOpen} onOpenChange={setFeeApplicationDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Apply Admin Fee Configuration
            </DialogTitle>
            <DialogDescription>
              Select which records should use the new admin fee configuration
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Fines Configuration Section */}
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-medium text-red-800 mb-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Fine Configuration
              </h4>
              <p className="text-sm text-red-700 mb-3">
                Admin Fee: {adminFeeConfig?.fines?.fee_type === 'percentage'
                  ? `${adminFeeConfig?.fines?.percentage || 0}% of fine amount`
                  : `Fixed Dh ${adminFeeConfig?.fines?.fixed_amount || 0}`}
              </p>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-2 rounded hover:bg-red-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={feeApplicationOptions.finesNewRecords}
                    onChange={() => toggleFeeOption('finesNewRecords')}
                    className="w-4 h-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-red-800">Apply to new Fine records</span>
                </label>
                <label className="flex items-center gap-3 p-2 rounded hover:bg-red-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={feeApplicationOptions.finesOldRecords}
                    onChange={() => toggleFeeOption('finesOldRecords')}
                    className="w-4 h-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-red-800">Apply to all existing Fine records</span>
                </label>
              </div>
            </div>

            {/* SALIK Configuration Section */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                SALIK Configuration
              </h4>
              <p className="text-sm text-purple-700 mb-3">
                Admin Charge: {adminFeeConfig?.salik?.fee_type === 'percentage'
                  ? `${adminFeeConfig?.salik?.percentage || 0}% of toll amount`
                  : `Fixed Dh ${adminFeeConfig?.salik?.fixed_amount || 0}`}
              </p>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-2 rounded hover:bg-purple-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={feeApplicationOptions.salikNewRecords}
                    onChange={() => toggleFeeOption('salikNewRecords')}
                    className="w-4 h-4 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-purple-800">Apply to new SALIK records</span>
                </label>
                <label className="flex items-center gap-3 p-2 rounded hover:bg-purple-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={feeApplicationOptions.salikOldRecords}
                    onChange={() => toggleFeeOption('salikOldRecords')}
                    className="w-4 h-4 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-purple-800">Apply to all existing SALIK records</span>
                </label>
              </div>
            </div>

            {/* Summary Section */}
            {hasSelectedOptions && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Summary of Changes
                </h4>
                <ul className="text-sm text-green-700 space-y-1 list-disc list-inside">
                  {feeApplicationOptions.finesNewRecords && (
                    <li>New fines will use: {adminFeeConfig?.fines?.fee_type === 'percentage'
                      ? `${adminFeeConfig?.fines?.percentage}% admin fee`
                      : `Dh ${adminFeeConfig?.fines?.fixed_amount} fixed admin fee`}</li>
                  )}
                  {feeApplicationOptions.finesOldRecords && (
                    <li className="text-orange-700">All existing fines will be recalculated with new admin fee</li>
                  )}
                  {feeApplicationOptions.salikNewRecords && (
                    <li>New SALIK records will use: {adminFeeConfig?.salik?.fee_type === 'percentage'
                      ? `${adminFeeConfig?.salik?.percentage}% admin charge`
                      : `Dh ${adminFeeConfig?.salik?.fixed_amount} fixed admin charge`}</li>
                  )}
                  {feeApplicationOptions.salikOldRecords && (
                    <li className="text-orange-700">All existing SALIK records will be recalculated with new admin charge</li>
                  )}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setFeeApplicationDialogOpen(false)}
                disabled={applyingToExisting}
              >
                Cancel
              </Button>
              <Button
                onClick={applyFeeConfiguration}
                disabled={!hasSelectedOptions || applyingToExisting}
                className="bg-green-600 hover:bg-green-700"
              >
                {applyingToExisting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Applying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Apply Configuration
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Data Standardization Card */}
      {userCan(user, 'adminTools', 'view', 'settings') && (
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Database className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Data Standardization</CardTitle>
                  <CardDescription>
                    Standardize mobile numbers, Emirates IDs, and names across all records
                  </CardDescription>
                </div>
              </div>
              <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                Admin Only
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Left - Format Rules */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700">Standard Formats</h4>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Phone className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">UAE Mobile Number</p>
                      <p className="text-xs text-slate-500">Format: +971 XX XXX XXXX</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Example: 0501234567 → +971501234567
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <CreditCard className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Emirates ID</p>
                      <p className="text-xs text-slate-500">Format: 15 digits (no dashes)</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Example: 784-1234-1234567-1 → 784123412345671
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <User className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Name (Title Case)</p>
                      <p className="text-xs text-slate-500">First letter of each word capitalized</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Example: JOHN DOE → John Doe
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Actions */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700">Actions</h4>
                
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700 mb-3">
                    Preview the changes before applying them to ensure data accuracy.
                  </p>
                  <Button 
                    onClick={fetchStandardizationPreview}
                    disabled={loadingPreview}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="preview-standardization-btn"
                  >
                    {loadingPreview ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading Preview...
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-2" />
                        Preview Changes
                      </>
                    )}
                  </Button>
                </div>

                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="font-medium text-yellow-800 mb-2">⚠️ Important</h4>
                  <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                    <li>Always preview before migrating</li>
                    <li>Migration updates all existing records</li>
                    <li>Changes are permanent but non-destructive</li>
                    <li>New entries will auto-format on save</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Standardization Preview Dialog */}
      <Dialog open={standardizationPreviewOpen} onOpenChange={setStandardizationPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-600" />
              Data Standardization Preview
            </DialogTitle>
            <DialogDescription>
              Review the changes below before applying standardization
            </DialogDescription>
          </DialogHeader>
          
          {standardizationPreview && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs font-medium text-blue-800">Riders</p>
                  <p className="text-xl font-bold text-blue-600">
                    {standardizationPreview.ridersWithChanges}
                  </p>
                  <p className="text-xs text-blue-600">
                    of {standardizationPreview.totalRiders}
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-xs font-medium text-green-800">Staff</p>
                  <p className="text-xl font-bold text-green-600">
                    {standardizationPreview.staffWithChanges || 0}
                  </p>
                  <p className="text-xs text-green-600">
                    of {standardizationPreview.totalStaff || 0}
                  </p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-xs font-medium text-purple-800">TAP Candidates</p>
                  <p className="text-xl font-bold text-purple-600">
                    {standardizationPreview.tapWithChanges || 0}
                  </p>
                  <p className="text-xs text-purple-600">
                    of {standardizationPreview.totalTapCandidates || 0}
                  </p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-xs font-medium text-orange-800">Lessees</p>
                  <p className="text-xl font-bold text-orange-600">
                    {standardizationPreview.lesseesWithChanges || 0}
                  </p>
                  <p className="text-xs text-orange-600">
                    of {standardizationPreview.totalLessees || 0}
                  </p>
                </div>
              </div>

              {/* Rider Changes Preview */}
              {standardizationPreview.riders?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-blue-700">Rider Changes (sample)</h4>
                  <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-blue-50">
                          <TableHead className="w-[100px]">Field</TableHead>
                          <TableHead>Before</TableHead>
                          <TableHead className="w-[40px]"></TableHead>
                          <TableHead>After</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {standardizationPreview.riders.map((rider, idx) => (
                          <React.Fragment key={`rider-${rider.id}-${idx}`}>
                            {rider.name.changed && (
                              <TableRow className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                <TableCell className="font-medium text-xs">Name</TableCell>
                                <TableCell className="text-sm text-red-600">{rider.name.before || '-'}</TableCell>
                                <TableCell><ArrowRight className="w-4 h-4 text-slate-400" /></TableCell>
                                <TableCell className="text-sm text-green-600 font-medium">{rider.name.after}</TableCell>
                              </TableRow>
                            )}
                            {rider.mobile.changed && (
                              <TableRow className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                <TableCell className="font-medium text-xs">Mobile</TableCell>
                                <TableCell className="text-sm text-red-600 font-mono">{rider.mobile.before || '-'}</TableCell>
                                <TableCell><ArrowRight className="w-4 h-4 text-slate-400" /></TableCell>
                                <TableCell className="text-sm text-green-600 font-mono font-medium">{rider.mobile.after}</TableCell>
                              </TableRow>
                            )}
                            {rider.emirates_id.changed && (
                              <TableRow className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                <TableCell className="font-medium text-xs">Emirates ID</TableCell>
                                <TableCell className="text-sm text-red-600 font-mono">{rider.emirates_id.before || '-'}</TableCell>
                                <TableCell><ArrowRight className="w-4 h-4 text-slate-400" /></TableCell>
                                <TableCell className="text-sm text-green-600 font-mono font-medium">{rider.emirates_id.after}</TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Staff Changes Preview */}
              {standardizationPreview.staff?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-green-700">Staff Changes (sample)</h4>
                  <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-green-50">
                          <TableHead className="w-[100px]">Field</TableHead>
                          <TableHead>Before</TableHead>
                          <TableHead className="w-[40px]"></TableHead>
                          <TableHead>After</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {standardizationPreview.staff.map((emp, idx) => (
                          <React.Fragment key={`staff-${emp.id}-${idx}`}>
                            {emp.name.changed && (
                              <TableRow className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                <TableCell className="font-medium text-xs">Name</TableCell>
                                <TableCell className="text-sm text-red-600">{emp.name.before || '-'}</TableCell>
                                <TableCell><ArrowRight className="w-4 h-4 text-slate-400" /></TableCell>
                                <TableCell className="text-sm text-green-600 font-medium">{emp.name.after}</TableCell>
                              </TableRow>
                            )}
                            {emp.mobile.changed && (
                              <TableRow className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                <TableCell className="font-medium text-xs">Mobile</TableCell>
                                <TableCell className="text-sm text-red-600 font-mono">{emp.mobile.before || '-'}</TableCell>
                                <TableCell><ArrowRight className="w-4 h-4 text-slate-400" /></TableCell>
                                <TableCell className="text-sm text-green-600 font-mono font-medium">{emp.mobile.after}</TableCell>
                              </TableRow>
                            )}
                            {emp.emirates_id.changed && (
                              <TableRow className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                <TableCell className="font-medium text-xs">Emirates ID</TableCell>
                                <TableCell className="text-sm text-red-600 font-mono">{emp.emirates_id.before || '-'}</TableCell>
                                <TableCell><ArrowRight className="w-4 h-4 text-slate-400" /></TableCell>
                                <TableCell className="text-sm text-green-600 font-mono font-medium">{emp.emirates_id.after}</TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* TAP Candidates Changes Preview */}
              {standardizationPreview.tapCandidates?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-purple-700">TAP Candidate Changes (sample)</h4>
                  <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-purple-50">
                          <TableHead className="w-[100px]">Field</TableHead>
                          <TableHead>Before</TableHead>
                          <TableHead className="w-[40px]"></TableHead>
                          <TableHead>After</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {standardizationPreview.tapCandidates.map((candidate, idx) => (
                          <React.Fragment key={`tap-${candidate.id}-${idx}`}>
                            {candidate.name.changed && (
                              <TableRow className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                <TableCell className="font-medium text-xs">Name</TableCell>
                                <TableCell className="text-sm text-red-600">{candidate.name.before || '-'}</TableCell>
                                <TableCell><ArrowRight className="w-4 h-4 text-slate-400" /></TableCell>
                                <TableCell className="text-sm text-green-600 font-medium">{candidate.name.after}</TableCell>
                              </TableRow>
                            )}
                            {candidate.mobile.changed && (
                              <TableRow className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                <TableCell className="font-medium text-xs">Mobile</TableCell>
                                <TableCell className="text-sm text-red-600 font-mono">{candidate.mobile.before || '-'}</TableCell>
                                <TableCell><ArrowRight className="w-4 h-4 text-slate-400" /></TableCell>
                                <TableCell className="text-sm text-green-600 font-mono font-medium">{candidate.mobile.after}</TableCell>
                              </TableRow>
                            )}
                            {candidate.emirates_id.changed && (
                              <TableRow className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                <TableCell className="font-medium text-xs">Emirates ID</TableCell>
                                <TableCell className="text-sm text-red-600 font-mono">{candidate.emirates_id.before || '-'}</TableCell>
                                <TableCell><ArrowRight className="w-4 h-4 text-slate-400" /></TableCell>
                                <TableCell className="text-sm text-green-600 font-mono font-medium">{candidate.emirates_id.after}</TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Lessee Changes Preview */}
              {standardizationPreview.lessees?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-orange-700">Lessee Changes (sample)</h4>
                  <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-orange-50">
                          <TableHead className="w-[100px]">Field</TableHead>
                          <TableHead>Before</TableHead>
                          <TableHead className="w-[40px]"></TableHead>
                          <TableHead>After</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {standardizationPreview.lessees.map((lessee, idx) => (
                          <React.Fragment key={`lessee-${lessee.id}-${idx}`}>
                            {lessee.name.changed && (
                              <TableRow className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                <TableCell className="font-medium text-xs">Name</TableCell>
                                <TableCell className="text-sm text-red-600">{lessee.name.before || '-'}</TableCell>
                                <TableCell><ArrowRight className="w-4 h-4 text-slate-400" /></TableCell>
                                <TableCell className="text-sm text-green-600 font-medium">{lessee.name.after}</TableCell>
                              </TableRow>
                            )}
                            {lessee.mobile.changed && (
                              <TableRow className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                <TableCell className="font-medium text-xs">Phone</TableCell>
                                <TableCell className="text-sm text-red-600 font-mono">{lessee.mobile.before || '-'}</TableCell>
                                <TableCell><ArrowRight className="w-4 h-4 text-slate-400" /></TableCell>
                                <TableCell className="text-sm text-green-600 font-mono font-medium">{lessee.mobile.after}</TableCell>
                              </TableRow>
                            )}
                            {lessee.contact_person?.changed && (
                              <TableRow className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                <TableCell className="font-medium text-xs">Contact Person</TableCell>
                                <TableCell className="text-sm text-red-600">{lessee.contact_person.before || '-'}</TableCell>
                                <TableCell><ArrowRight className="w-4 h-4 text-slate-400" /></TableCell>
                                <TableCell className="text-sm text-green-600 font-medium">{lessee.contact_person.after}</TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* No changes message */}
              {(!standardizationPreview.riders?.length && !standardizationPreview.staff?.length && 
                !standardizationPreview.tapCandidates?.length && !standardizationPreview.lessees?.length) && (
                <div className="p-8 text-center bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-green-800 font-medium">All data is already standardized!</p>
                  <p className="text-green-600 text-sm mt-1">No changes needed.</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setStandardizationPreviewOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={runDataStandardization}
                  disabled={migratingData || (!standardizationPreview.riders?.length && !standardizationPreview.staff?.length && 
                    !standardizationPreview.tapCandidates?.length && !standardizationPreview.lessees?.length)}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="run-standardization-btn"
                >
                  {migratingData ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Migrating...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4 mr-2" />
                      Apply Standardization
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Digest Email Preview
            </DialogTitle>
            <DialogDescription>
              This is what your daily digest email would look like
            </DialogDescription>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Recipient:</span>
                    <p className="font-medium">{previewData.user_name} ({previewData.user_email})</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Total Alerts:</span>
                    <p className="font-medium">{previewData.total_alerts}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Would Send:</span>
                    <Badge className={previewData.would_send ? 'ml-2 bg-green-100 text-green-700' : 'ml-2 bg-red-100 text-red-700'}>
                      {previewData.would_send ? 'Yes' : 'No (no alerts)'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Alerts Breakdown */}
              <div className="space-y-3">
                {/* Battery Mismatch Alerts */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-red-50 border-b flex items-center justify-between">
                    <span className="font-medium text-red-800">🔋 Battery Mismatch Alerts</span>
                    <Badge variant="outline" className="text-red-600">
                      {previewData.alerts?.battery_mismatch?.length || 0}
                    </Badge>
                  </div>
                  <div className="p-3 text-sm">
                    {previewData.alerts?.battery_mismatch?.length > 0 ? (
                      <ul className="space-y-1">
                        {previewData.alerts.battery_mismatch.slice(0, 5).map((alert, i) => (
                          <li key={i} className="text-slate-600">
                            • {alert.rider_name || 'Unknown'} - {alert.bike_number || 'N/A'}
                          </li>
                        ))}
                        {previewData.alerts.battery_mismatch.length > 5 && (
                          <li className="text-slate-400">...and {previewData.alerts.battery_mismatch.length - 5} more</li>
                        )}
                      </ul>
                    ) : (
                      <p className="text-slate-400">No battery mismatch alerts</p>
                    )}
                  </div>
                </div>

                {/* Document Expiry Alerts */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-orange-50 border-b flex items-center justify-between">
                    <span className="font-medium text-orange-800">📄 Document Expiry Alerts</span>
                    <Badge variant="outline" className="text-orange-600">
                      {previewData.alerts?.document_expiry?.length || 0}
                    </Badge>
                  </div>
                  <div className="p-3 text-sm">
                    {previewData.alerts?.document_expiry?.length > 0 ? (
                      <ul className="space-y-1">
                        {previewData.alerts.document_expiry.slice(0, 5).map((alert, i) => (
                          <li key={i} className="text-slate-600">
                            • {alert.document_type}: {alert.name} ({alert.days_left} days)
                          </li>
                        ))}
                        {previewData.alerts.document_expiry.length > 5 && (
                          <li className="text-slate-400">...and {previewData.alerts.document_expiry.length - 5} more</li>
                        )}
                      </ul>
                    ) : (
                      <p className="text-slate-400">No document expiry alerts</p>
                    )}
                  </div>
                </div>

                {/* Maintenance Alerts */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-blue-50 border-b flex items-center justify-between">
                    <span className="font-medium text-blue-800">🔧 Maintenance Alerts</span>
                    <Badge variant="outline" className="text-blue-600">
                      {previewData.alerts?.maintenance?.length || 0}
                    </Badge>
                  </div>
                  <div className="p-3 text-sm">
                    {previewData.alerts?.maintenance?.length > 0 ? (
                      <ul className="space-y-1">
                        {previewData.alerts.maintenance.slice(0, 5).map((alert, i) => (
                          <li key={i} className="text-slate-600">
                            • {alert.bike_plate}: {alert.issue} ({alert.status})
                          </li>
                        ))}
                        {previewData.alerts.maintenance.length > 5 && (
                          <li className="text-slate-400">...and {previewData.alerts.maintenance.length - 5} more</li>
                        )}
                      </ul>
                    ) : (
                      <p className="text-slate-400">No pending maintenance alerts</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
};

export default Settings;
